/**
 * El teclado fisico: Enter confirma, Escape cierra, Espacio hace lo suyo.
 *
 * POR QUE EXISTE. En la all-in-one del mostrador quien cobra tiene el teclado
 * enfrente y tenia que soltarlo y apuntar con el raton en cada "Agregar" y en
 * cada "Cobrar" — medido en esta tienda, 3.72 renglones por venta y 80.8 % de
 * granel, o sea que la hoja de cantidad se abre en cuatro de cada cinco
 * productos. Lo que esta capa compra es lo que hace un punto de venta de verdad:
 * teclear, Enter, teclear, Enter, y cerrar la venta sin apartar la mano.
 *
 * ESTO VIVE ENCIMA DEL LECTOR USB, Y ESO MANDA SOBRE EL DISEÑO
 * ------------------------------------------------------------
 * `lib/scanner.ts` tiene el otro oyente de teclado, en CAPTURA de ventana, y
 * distingue al lector de una persona por el ritmo. Tres hechos:
 *
 *  1. En una lectura valida el escaner hace `stopPropagation()`, y desde captura
 *     eso corta la cadena entera. Asi que un oyente en BURBUJA de ventana —el
 *     ultimo de todos— NUNCA ve el Enter de un escaneo. Por eso esta capa
 *     escucha ahi y no en captura: es lo que hace seguro que Enter signifique
 *     "Cobrar" sin que un disparo del lector cobre.
 *  2. Los CARACTERES de una racha si llegan a burbuja: el escaner solo les hace
 *     `preventDefault`, que cancela la insercion en el campo pero no detiene la
 *     propagacion. De ahi que el teclado en pantalla necesite su propia guarda;
 *     vive en `useKeypad` (components/NumericPad.tsx) y pregunta por
 *     `machineRun()`.
 *  3. React 19 monta sus oyentes delegados en el contenedor raiz y en cada
 *     contenedor de portal —`Modal` va a `document.body`—, los dos por debajo de
 *     `window`. Cuando corre esto, todo `onKeyDown` de React ya se ejecuto.
 *
 * De (3) sale EL CONTRATO, y hay que respetarlo al escribir cualquier campo:
 *
 *     Un campo que se queda con su Enter hace `preventDefault()` y JAMAS
 *     `stopPropagation()`. Uno que no lo quiere, lo deja pasar limpio.
 *
 * Lo unico que esta capa puede mirar es `defaultPrevented`, asi que un campo que
 * "se queda" con la tecla sin marcarlo hace que la tecla ocurra DOS VECES. Paso
 * con el editor de cantidad del ticket, que soltaba el foco sin `preventDefault`
 * y por tanto ademas habria abierto el cobro. Lo vigila
 * `styles/enter-contract.test.ts`.
 *
 * CAPAS, LAS MISMAS DEL LECTOR
 * ----------------------------
 * Se reusa `useScanLayer()` de `scanner.ts` a proposito: es literalmente la misma
 * pila —la pantalla es 0 y cada hoja modal suma una— y tener dos seria tener dos
 * ideas de "que hay encima". Solo el nivel de mas arriba recibe teclas; lo de
 * abajo queda tapado aunque no haya nadie que atienda esa tecla en particular.
 * Sin eso, un Enter dentro de una hoja abriria el cobro de la pantalla de atras.
 *
 * Dentro de un mismo nivel se resuelve POR TECLA: `Modal` se registra como marco
 * (prioridad 0) con su Escape, y el contenido de la hoja se registra como
 * contenido (prioridad 1). Asi el abono de la ficha de cliente —que es un hijo
 * con su propio estado, no el pie de la hoja— se queda con Enter sin quitarle el
 * Escape a su Modal, y sin que ninguno de los dos sepa del otro.
 */

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useScanLayer } from './scanner.js';

// ---------------------------------------------------------------------------
// Lo que una capa puede pedir
// ---------------------------------------------------------------------------

export interface KeyBindings {
  /** La accion principal de la hoja: Agregar, Cobrar, Guardar, Listo. */
  onEnter?: () => void;
  /** La segunda salida, cuando la hoja tiene dos. Opt-in, nunca global. */
  onSpace?: () => void;
  onEscape?: () => void;
  /**
   * Teclas de escritura —digitos, punto, coma, borrar— para un teclado en
   * pantalla. Llegan CRUDAS (`key` y `code`); traducirlas es de quien las pide,
   * porque solo el pad sabe que es una tecla suya.
   */
  onPad?: (key: string, code: string) => void;
  /**
   * Enter dentro de un campo confirma la hoja. `true` por omision, que es lo que
   * hace un formulario de siempre.
   *
   * `false` en las hojas-DOCUMENTO —la entrada de mercancia, el alta de
   * producto—, donde el Enter de un campo a media captura enviaria medio
   * documento. Ahi Enter dentro de un campo lo CONFIRMA (suelta el foco) y solo
   * confirma la hoja con el foco fuera.
   */
  enterInFields?: boolean;
}

/** El marco de la hoja (su Escape) pierde contra su contenido. */
const FRAME = 0;
const CONTENT = 1;

interface Target {
  layer: number;
  priority: number;
  seq: number;
  readonly bindings: KeyBindings;
}

// ---------------------------------------------------------------------------
// La decision, sin DOM
// ---------------------------------------------------------------------------

/**
 * Que hay bajo el foco, reducido a las tres cosas que cambian la respuesta.
 *
 * Es una FORMA y no un elemento para que la decision se pueda probar en Node,
 * que es como se prueba todo en este repositorio.
 */
export interface FocusShape {
  /** Se escribe ahi: input, textarea, select, contenteditable. */
  editable: boolean;
  /** Enter ESCRIBE ahi: textarea y contenteditable. Nunca se le quita. */
  writesEnter: boolean;
  /** Enter y Espacio ya significan "pulsame": boton, enlace, summary. */
  activatable: boolean;
}

export interface KeyShape {
  key: string;
  code: string;
  repeat: boolean;
  isComposing: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  defaultPrevented: boolean;
  focus: FocusShape;
}

export type KeyAction =
  | { kind: 'none' }
  | { kind: 'enter'; run: () => void }
  | { kind: 'space'; run: () => void }
  | { kind: 'escape'; run: () => void }
  | { kind: 'pad'; run: () => void }
  /** Enter en un campo de una hoja-documento: confirma el dato, no la hoja. */
  | { kind: 'blur' };

const NONE: KeyAction = { kind: 'none' };

/**
 * Teclas de escritura que le interesan a un teclado numerico.
 *
 * Espacio, Enter y Escape quedan fuera aunque `' '` mida un caracter: son
 * ordenes, no escritura, y cada una tiene su propia rama mas arriba.
 */
function isTypingKey(key: string): boolean {
  if (key === ' ' || key === 'Enter' || key === 'Escape') return false;
  return key.length === 1 || key === 'Backspace' || key === 'Delete';
}

/**
 * A quien le toca esta tecla.
 *
 * Primero se acota al nivel de mas arriba —eso es lo que BLOQUEA lo de abajo— y
 * dentro de el gana quien defina la tecla, por prioridad y despues por orden de
 * registro. Si nadie en ese nivel la define, no la atiende nadie: la capa de
 * abajo NO hereda.
 */
function winner(
  targets: readonly Target[],
  defines: (bindings: KeyBindings) => boolean,
): Target | null {
  let top = -Infinity;
  for (const target of targets) if (target.layer > top) top = target.layer;

  let best: Target | null = null;
  for (const target of targets) {
    if (target.layer !== top || !defines(target.bindings)) continue;
    if (best === null || target.priority > best.priority) best = target;
    else if (target.priority === best.priority && target.seq > best.seq) best = target;
  }
  return best;
}

export function resolveKey(event: KeyShape, targets: readonly Target[]): KeyAction {
  /*
   * `repeat` es una tecla detenida: una sola pulsacion no puede cobrar dos
   * veces. `isComposing` es una tecla muerta de un teclado español —el Enter que
   * remata un acento no es un Enter, y sin esto un acento cerraria una venta—.
   * El escaner ya se protege de las dos, y esta capa tiene que protegerse igual.
   */
  if (event.repeat || event.isComposing) return NONE;
  if (event.ctrlKey || event.metaKey || event.altKey) return NONE;
  if (targets.length === 0) return NONE;

  /*
   * `defaultPrevented` es el contrato con los campos: quien se queda con una
   * tecla lo marca asi, y aqui se respeta.
   *
   * PERO NO VALE PARA LAS TECLAS DE ESCRITURA, y la excepcion no es una
   * comodidad: es lo que hace funcionar la guarda del teclado en pantalla. El
   * escaner le hace `preventDefault` a los caracteres de una racha a partir del
   * sexto —para que no se cuelen en el campo enfocado— y ese sexto es
   * exactamente la tecla en la que `useKeypad` tiene que enterarse de que lo que
   * esta llegando es una lectura, para devolver la cifra a como estaba. Filtrando
   * aqui, esa tecla nunca llegaba: se probo en el navegador con una racha de
   * trece digitos y la hoja se quedaba con los cinco primeros ESCRITOS.
   *
   * Es seguro porque la rama de escritura solo corre con el foco FUERA de todo
   * campo y de todo boton, y ahi el unico que hace `preventDefault` sobre un
   * digito es el escaner — que es justo de quien hay que enterarse.
   */
  if (event.defaultPrevented && !isTypingKey(event.key)) return NONE;

  const { focus } = event;

  if (event.key === 'Escape') {
    const run = winner(targets, (b) => b.onEscape !== undefined)?.bindings.onEscape;
    return run ? { kind: 'escape', run } : NONE;
  }

  if (event.key === 'Enter') {
    // Donde Enter escribe (una nota, un motivo) o donde ya significa "pulsame"
    // (un boton enfocado), esta capa se aparta. Lo del boton no es cortesia: el
    // navegador sintetiza el clic DESPUES de este oyente, asi que sin la guarda
    // un Enter con el foco en "Cancelar" cancelaria Y confirmaria.
    if (focus.writesEnter || focus.activatable) return NONE;

    const target = winner(targets, (b) => b.onEnter !== undefined);
    if (target === null) return NONE;
    if (focus.editable && target.bindings.enterInFields === false) return { kind: 'blur' };

    const run = target.bindings.onEnter;
    return run ? { kind: 'enter', run } : NONE;
  }

  if (event.key === ' ' || event.key === 'Spacebar') {
    // Espacio ya desplaza la hoja y ya pulsa el boton enfocado. Se le quita solo
    // donde no hace ninguna de las dos.
    if (focus.editable || focus.activatable) return NONE;
    const run = winner(targets, (b) => b.onSpace !== undefined)?.bindings.onSpace;
    return run ? { kind: 'space', run } : NONE;
  }

  if (isTypingKey(event.key)) {
    if (focus.editable || focus.activatable) return NONE;
    const onPad = winner(targets, (b) => b.onPad !== undefined)?.bindings.onPad;
    if (!onPad) return NONE;
    const { key, code } = event;
    return { kind: 'pad', run: () => onPad(key, code) };
  }

  return NONE;
}

// ---------------------------------------------------------------------------
// El registro y la escucha, una sola para toda la aplicacion
// ---------------------------------------------------------------------------

const targets = new Set<Target>();
let nextSeq = 0;
let listening = false;

// --- quien esta encima, para quien tenga que apartarse -----------------------

const oyentes = new Set<() => void>();

function avisar(): void {
  for (const oyente of oyentes) oyente();
}

function suscribir(oyente: () => void): () => void {
  oyentes.add(oyente);
  return () => oyentes.delete(oyente);
}

/** La capa mas alta registrada. 0 = no hay ninguna hoja abierta. */
function capaMasAlta(): number {
  let top = 0;
  for (const target of targets) if (target.layer > top) top = target.layer;
  return top;
}

/**
 * Que tan arriba llega lo que hay abierto ahora mismo.
 *
 * EXISTE PARA NO ENUMERAR HOJAS A MANO. La Caja tenia un efecto que le devolvia
 * el foco a su buscador "cuando no hay ninguna hoja abierta", y lo decidia con
 * una lista escrita a mano de cinco estados. Le faltaban las dos hojas que
 * aparecen DESPUES de cobrar —el acuse y el ticket—, asi que al cerrarse el cobro
 * el buscador le robaba el foco al acuse y Espacio escribia un espacio en vez de
 * abrir el ticket.
 *
 * Es la misma trampa que el lector USB ya habia pagado —cada pantalla apagando su
 * oyente con `!paymentOpen && !cameraOpen && ...`, y olvidar una dejaba un escaneo
 * cayendo detras de una hoja— y se arregla igual: preguntando por CAPAS. Una lista
 * a mano hay que acordarse de actualizarla; una capa se ocupa sola por el hecho de
 * abrir la hoja.
 */
export function useTopLayer(): number {
  return useSyncExternalStore(suscribir, capaMasAlta, capaMasAlta);
}

function describeFocus(node: EventTarget | null): FocusShape {
  const element = node instanceof HTMLElement ? node : null;
  if (element === null) return { editable: false, writesEnter: false, activatable: false };

  const tag = element.tagName;
  const contentEditable = element.isContentEditable === true;

  return {
    editable: contentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT',
    writesEnter: contentEditable || tag === 'TEXTAREA',
    activatable: element.closest('button, a[href], summary, [role="button"]') !== null,
  };
}

function onKeyDown(event: KeyboardEvent): void {
  const action = resolveKey(
    {
      key: event.key,
      code: event.code,
      repeat: event.repeat,
      isComposing: event.isComposing,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      altKey: event.altKey,
      defaultPrevented: event.defaultPrevented,
      focus: describeFocus(event.target),
    },
    [...targets],
  );

  if (action.kind === 'none') return;

  if (action.kind === 'blur') {
    event.preventDefault();
    const active = document.activeElement;
    if (active instanceof HTMLElement) active.blur();
    return;
  }

  /*
   * Escape se deja pasar tal cual —no tiene accion por omision que estorbe y asi
   * se comportaba antes—. Las otras tres SI la tienen y hay que cancelarla:
   * Espacio desplaza la hoja (imprimiria y saltaria de pagina a la vez),
   * Retroceso navega hacia atras en algunos navegadores, y la barra abre la
   * busqueda rapida de Firefox.
   */
  if (action.kind !== 'escape') event.preventDefault();
  action.run();
}

function startListening(): void {
  if (listening) return;
  listening = true;
  window.addEventListener('keydown', onKeyDown);
}

function stopListening(): void {
  if (!listening || targets.size > 0) return;
  listening = false;
  window.removeEventListener('keydown', onKeyDown);
}

function register(layer: number, priority: number, read: () => KeyBindings): () => void {
  nextSeq += 1;
  /*
   * Las asignaciones se LEEN cuando llega la tecla, no se copian al registrarse.
   * El padre vuelve a pintar en cada venta ajena que llega por Realtime, y con
   * una copia habria que re-registrar en cada repintado — lo que ademas moveria
   * el `seq` y podria cambiar quien gana un empate a media captura. Es el mismo
   * motivo por el que `useUsbScanner` guarda su callback en un ref.
   */
  const entry: Target = {
    layer,
    priority,
    seq: nextSeq,
    get bindings() {
      return read();
    },
  };
  targets.add(entry);
  startListening();
  avisar();

  return () => {
    targets.delete(entry);
    stopListening();
    avisar();
  };
}

function useRegister(bindings: KeyBindings, active: boolean, layer: number, priority: number): void {
  const ref = useRef(bindings);
  ref.current = bindings;

  useEffect(() => {
    if (!active) return;
    return register(layer, priority, () => ref.current);
  }, [active, layer, priority]);
}

/**
 * Atiende el teclado mientras el componente este montado y `active`.
 *
 * Se registra en la capa donde vive quien llama, asi que una hoja abierta le gana
 * a la pantalla de atras sin que ninguna de las dos tenga que enterarse de la
 * otra — igual que `useUsbScanner`.
 */
export function useKeys(bindings: KeyBindings, active = true): void {
  useRegister(bindings, active, useScanLayer(), CONTENT);
}

/**
 * Lo mismo, para el MARCO de una hoja modal. Lo usa `Modal` y nadie mas.
 *
 * Dos diferencias, las dos por la misma razon —el marco se escribe una capa mas
 * abajo de donde vive su contenido—:
 *
 *   - Se registra en `useScanLayer() + 1`, que es la capa que `Modal` le da a sus
 *     hijos. Sin el +1 su Escape quedaria en la capa de la PANTALLA y una segunda
 *     hoja encima no podria ganarle: un Escape cerraria las dos, que es justo el
 *     defecto que esto viene a arreglar. Es el mismo +1 de `useScanBlocker`.
 *   - Prioridad de marco: pierde contra el contenido de su propia hoja cuando los
 *     dos quieren la misma tecla.
 */
export function useFrameKeys(bindings: KeyBindings, active = true): void {
  useRegister(bindings, active, useScanLayer() + 1, FRAME);
}
